import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';

import Icon from '../../components/AppIcon';
import NotificationSettings from './components/NotificationSettings';
import PrivacyControls from './components/PrivacyControls';
import ProfileVisibility from './components/ProfileVisibility';
import CommunicationPreferences from './components/CommunicationPreferences';
import AdvancedSettings from './components/AdvancedSettings';
import CompanyBlockingSettings from './components/CompanyBlockingSettings';
import { userSession, matches } from '../../services/localStorageService';
import { userPreferencesService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const UserPreferencesCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('notifications');
  const [saveMessage, setSaveMessage] = useState(null);

  const currentUser = userSession?.get();
  const matchCount = matches?.getAll()?.filter(m => m?.userId === currentUser?.id)?.length || 0;

  // Load user preferences from Supabase
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const prefs = await userPreferencesService?.get(user?.id);
        // Preferences will be loaded by individual components
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  const handleSave = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSaveSuccess = (message) => {
    setSaveMessage({ type: 'success', text: message });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const tabs = [
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' },
    { id: 'visibility', label: 'Profile Visibility', icon: 'Shield' },
    { id: 'communication', label: 'Communication', icon: 'MessageSquare' },
    { id: 'blocking', label: 'Company Blocking', icon: 'Ban' },
    { id: 'advanced', label: 'Advanced', icon: 'Settings' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'notifications':
        return <NotificationSettings userRole={currentUser?.userType} onSave={handleSaveSuccess} />;
      case 'privacy':
        return <PrivacyControls userRole={currentUser?.userType} onSave={handleSaveSuccess} />;
      case 'visibility':
        return <ProfileVisibility userRole={currentUser?.userType} onSave={handleSaveSuccess} />;
      case 'communication':
        return <CommunicationPreferences userRole={currentUser?.userType} onSave={handleSaveSuccess} />;
      case 'blocking':
        return <CompanyBlockingSettings userRole={currentUser?.userType} onSave={handleSaveSuccess} />;
      case 'advanced':
        return <AdvancedSettings userRole={currentUser?.userType} onSave={handleSaveSuccess} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole={currentUser?.userType} matchCount={matchCount} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-white">
            <p>Loading preferences...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole={currentUser?.userType} matchCount={matchCount} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-400">
            <p>Error loading preferences: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] pb-20 lg:pb-0 lg:pl-60">
      <AuthenticatedNavigation 
        userRole={currentUser?.userType} 
        matchCount={matchCount}
      />

      <div className="pl-0 lg:pl-[280px] pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-[#5B2DFF] to-[#7C3FFF] rounded-xl">
                <Icon name="Settings" size={24} color="#FFFFFF" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#111827]">
                Preferences Center
              </h1>
            </div>
            <p className="text-sm md:text-base text-[#6B7280]">
              Manage your notification settings, privacy controls, and communication preferences
            </p>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`mb-4 p-4 rounded-xl border ${
              saveMessage?.type === 'success' ?'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
            } flex items-center gap-3`}>
              <Icon 
                name={saveMessage?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                size={20} 
                color={saveMessage?.type === 'success' ? '#16a34a' : '#dc2626'}
              />
              <span className="text-sm font-medium">{saveMessage?.text}</span>
            </div>
          )}

          {/* Tabs Navigation - Desktop */}
          <div className="hidden md:block bg-white rounded-2xl border border-[#E5E7EB] p-2 mb-6">
            <div className="flex gap-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-all ${
                    activeTab === tab?.id
                      ? 'bg-gradient-to-br from-[#5B2DFF] to-[#7C3FFF] text-white shadow-lg'
                      : 'text-[#6B7280] hover:bg-[#F6F7FB]'
                  }`}
                >
                  <Icon 
                    name={tab?.icon} 
                    size={18} 
                    color={activeTab === tab?.id ? '#FFFFFF' : '#6B7280'}
                  />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tabs Navigation - Mobile (Dropdown) */}
          <div className="md:hidden mb-6">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e?.target?.value)}
              className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-xl text-[#111827] font-medium focus:outline-none focus:ring-2 focus:ring-[#5B2DFF]"
            >
              {tabs?.map((tab) => (
                <option key={tab?.id} value={tab?.id}>
                  {tab?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 md:p-8">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPreferencesCenter;